import React from "react";
import { Checkbox } from "@client.components/Checkbox";
import UserNotificationTypes from "@client.enums/userNotificationTypes";
import { formatDate } from "@client.utils/dateUtils";
import { DateFormat } from "@client.enums/dateTypes";
import RecurringTypes from "@client.enums/recurringTypes";
import Button from "@client.components/Button";
import sparklesIcon from "@client.images/sparkles.svg";
import UserRoles from "@client.enums/userRoles";

const getSuffix = (recurringType) => {
  switch (recurringType) {
    case RecurringTypes.BiWeekly:
      return "biweekly";
    case RecurringTypes.Weekly:
      return "weekly";
    case RecurringTypes.Monthly:
      return "monthly";
    case RecurringTypes.Quaterly:
      return "quaterly";
    case RecurringTypes.All:
      return "all";
  }
}

// Deterministic hash function to convert insight ID to an index (0-9)
// This ensures the same insight always gets the same recommendation
const getHashIndex = (id) => {
  if (!id) return 0;
  let hash = 0;
  const str = String(id);
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash;
  }
  return Math.abs(hash) % 10;
}

const RECOMMENDATION_BASE_KEYS = {
  [UserNotificationTypes.EmployeeOverspendInternal]: "pages.Dashboard.Insights.recommendations.internal",
  [UserNotificationTypes.ScheduleOvercrowdedMeeting]: "pages.Dashboard.Insights.recommendations.noOfParticipants",
  [UserNotificationTypes.ScheduleHighInvestmentMeeting]: "pages.Dashboard.Insights.recommendations.highInvestment",
  [UserNotificationTypes.ScheduleRecurringMeeting]: "pages.Dashboard.Insights.recommendations.recurring",
  [UserNotificationTypes.EmployeeOverspendExternal]: "pages.Dashboard.Insights.recommendations.external",
  [UserNotificationTypes.LowAttendance]: "pages.Dashboard.Insights.recommendations.lowAttendance",
  [UserNotificationTypes.AIMeetingSimilaritiesRecognition]: "pages.Dashboard.Insights.recommendations.aiMeetingSimilaritiesRecognition",
  [UserNotificationTypes.AIPredictions]: "pages.Dashboard.Insights.recommendations.aiPredictions",
};

const RECOMMENDATION_INDICES = Array.from({ length: 10 }, (_, i) => i);

const getRecommendationPhrases = (t) => {
  return Object.fromEntries(
    Object.entries(RECOMMENDATION_BASE_KEYS).map(([type, baseKey]) => [
      type,
      RECOMMENDATION_INDICES.map((i) => t(`${baseKey}.${i}`))
    ])
  );
};

export const getNotificationTypeLabelsMap = (t) => {
  const recommendationPhrases = getRecommendationPhrases(t);
  const getRecommendation =
    (type) =>
    (insightId) => {
      const phrases = recommendationPhrases[type];
      const index = getHashIndex(insightId);
      return phrases[index] || phrases[0];
    };
  
  return {
    [UserNotificationTypes.EmployeeOverspendInternal]: {
      type: t("pages.Dashboard.Insights.types.internal"),
      getRecommendation: getRecommendation(UserNotificationTypes.EmployeeOverspendInternal),
      getDescription: (payload) => t(`pages.Dashboard.Insights.description.overspend.internal.${getSuffix(payload.recurringType)}`, {
        hours: Math.round(payload.hours),
      })
    },
    [UserNotificationTypes.ScheduleOvercrowdedMeeting]: {
      type: t("pages.Dashboard.Insights.types.noOfParticipants"),
      getRecommendation: getRecommendation(UserNotificationTypes.ScheduleOvercrowdedMeeting),
      getDescription: (payload) =>
        t("pages.Dashboard.Insights.description.noOfParticipants", payload),
    },
    [UserNotificationTypes.ScheduleHighInvestmentMeeting]: {
      type: t("pages.Dashboard.Insights.types.highInvestment"),
      getRecommendation: getRecommendation(UserNotificationTypes.ScheduleHighInvestmentMeeting),
      getDescription: (payload) =>
        t("pages.Dashboard.Insights.description.highInvestment", {
          price: Math.round(payload.price),
        }),
    },
    [UserNotificationTypes.ScheduleRecurringMeeting]: {
      type: t("pages.Dashboard.Insights.types.recurring"),
      getRecommendation: getRecommendation(UserNotificationTypes.ScheduleRecurringMeeting),
      getDescription: (payload) => {
        return t(
          `pages.Dashboard.Insights.description.recurring.${getSuffix(payload.recurringType)}`,
          payload
        );
      },
    },
    [UserNotificationTypes.EmployeeOverspendExternal]: {
      type: t("pages.Dashboard.Insights.types.external"),
      getRecommendation: getRecommendation(UserNotificationTypes.EmployeeOverspendExternal),
      getDescription: (payload) =>
        t(`pages.Dashboard.Insights.description.overspend.external.${getSuffix(payload.recurringType)}`, {
          hours: Math.round(payload.hours),
        }),
    },
    [UserNotificationTypes.LowAttendance]: {
      type: t("pages.Dashboard.Insights.types.lowAttendance"),
      getRecommendation: getRecommendation(UserNotificationTypes.LowAttendance),
      getDescription: (payload) =>
        t("pages.Dashboard.Insights.description.lowAttendance", {
          acceptedPercentage: Math.round(payload.acceptedPercentage),
        }),
    },
    [UserNotificationTypes.AIMeetingSimilaritiesRecognition]: {
      type: t("pages.Dashboard.Insights.types.aiMeetingSimilaritiesRecognition"),
      getRecommendation: getRecommendation(UserNotificationTypes.AIMeetingSimilaritiesRecognition),
      getDescription: (payload) =>
        t("pages.Dashboard.Insights.description.aiMeetingSimilaritiesRecognition", payload),
    },
    [UserNotificationTypes.AIPredictions]: {
      type: t("pages.Dashboard.Insights.types.aiPredictions"),
      getRecommendation: getRecommendation(UserNotificationTypes.AIPredictions),
      getDescription: (payload) => {
        const periodTerms = {
          3: "pages.Dashboard.Insights.description.aiPredictions.period.quarter",
          4: "pages.Dashboard.Insights.description.aiPredictions.period.month",
          6: "pages.Dashboard.Insights.description.aiPredictions.period.week",
        }
        return t("pages.Dashboard.Insights.description.aiPredictions", { ...payload, period: t(periodTerms[payload.type]) })
      },
    },
  };
};

const InsightTypesWithDetails = [UserNotificationTypes.EmployeeOverspendExternal, UserNotificationTypes.EmployeeOverspendInternal, UserNotificationTypes.LowAttendance, UserNotificationTypes.AIMeetingSimilaritiesRecognition];

export const getInsightsColumns = (t, lastInsightsUpdateAt, onChange, navigate, loadInsightEvents, userRole) => [
  {
    field: "isRead",
    headerName: "",
    maxWidth: 50,
    cellRenderer: ({ data }) => {
      return lastInsightsUpdateAt > data.createdAt ? null : (
        <Checkbox value={data.isRead} onChange={(v) => onChange(data.id, v)} />
      );
    },
  },
  {
    field: "typeDescription",
    tooltipField: "typeDescription",
    maxWidth: 150,
    headerName: t("pages.Dashboard.Insights.grid.columns.insightType"),
    cellRenderer: ({ data, value }) => {
      const showSparkles =
        data?.type === UserNotificationTypes.AIMeetingSimilaritiesRecognition ||
        data?.type === UserNotificationTypes.AIPredictions;
      return (
        <div className="insight-type-cell">
          {showSparkles && (
            <img
              src={sparklesIcon}
              alt="AI Sparkles  "
              className="insight-type-icon"
            />
          )}
          <span>{value}</span>
        </div>
      );
    },  
    cellClassRules: {
      "line-through": (params) => params.data.isRead,
    },
  },
  {
    field: "employee",
    tooltipField: "employee",
    maxWidth: 150,
    headerName: t("pages.Dashboard.Insights.grid.columns.employee"),
    cellClassRules: {
      "line-through": (params) => params.data.isRead,
    },
  },
  {
    field: "description",
    headerClass: "data-table-header header-center",
    headerName: t("pages.Dashboard.Insights.grid.columns.description"),
    wrapText: true,
    autoHeight: true,
    cellClassRules: {
      "line-through": (params) => params.data.isRead,
    },
  },
  {
    field: "recommendation",
    headerName: t("pages.Dashboard.Insights.grid.columns.recommendation"),
    wrapText: true,
    autoHeight: true,
    cellRenderer: ({ data, value }) => {
      if (data?.type === UserNotificationTypes.AIPredictions && data?.payload?.recommendation) {
        return data.payload.recommendation;
      }
      return value;
    },
    cellClassRules: {
      "line-through": (params) => params.data.isRead,
    },
  },
  {
    field: "createdAt",
    tooltipField: "createdAt",
    maxWidth: 120,
    headerName: t("pages.Dashboard.Insights.grid.columns.createdAt"),
    cellClassRules: {
      "line-through": (params) => params.data.isRead,
    },
    valueFormatter: (params) => {
      if (params.value) {
        const formattedDate = formatDate(params.value, DateFormat);
        return formattedDate;
      }
      return "";
    },
  },
  {
    wrapText: true,
    autoHeight: true,
    maxWidth: 120,
    cellClassRules: {
      "details-cell": () => true,
    },
    cellRenderer: ({ data}) => {
      const isDetailsButton = InsightTypesWithDetails.includes(data.type);
      return userRole === UserRoles.Viewer && !isDetailsButton ? null : <Button
        icon={isDetailsButton ? "reports" : "export-share"}
        text={t("pages.Dashboard.Insights.grid.details")}
        onClick={() => {
          isDetailsButton ? loadInsightEvents(data) : navigate(data)
        }}
        className="button-details"
        disabled={data.isRead}
      />
    }
  },
];
